import { Controller } from "@hotwired/stimulus"
import { openChrome, closeChrome, closeChromeThenNavigate, openOverlayScrim } from "../helpers/overlay_chrome"
import { paidIds, markPaid, prunePaid } from "../helpers/due_today_paid"
import { moneyText } from "../helpers/money_text"
import { rollDigits, ROLL_STAGGER_MS } from "../helpers/digit_roll"

// The Due today deck (budgets/_due_deck).
//
// Opens by itself once a day on the dashboard (autoValue, when no other card
// claimed the render), and on demand from the Due today card on any budget
// panel (due-deck:open). Either way it holds what is due across every budget.
// The cards in a sitting are fixed when it opens; each one leaves through one
// of three doors:
//
//   Skip  POST skip  — flies out the start edge, where its tile sits
//   Stop  POST stop  — flies out the end edge
//   Paid  POST paid  — banks the streak, hidden via localStorage
//         (helpers/due_today_paid); drops out the bottom
//
// Swiping toward a button does what it does: toward the start edge skips,
// toward the end edge stops, down marks paid.
//
// All three celebrate only once the server has accepted them (_settle).
//
// Each action plays the Due today row celebration inside the card, copied
// from skip_celebration_controller and run at twice its speed:
//
//   0     success haptic
//   10    the card washes toward the action's colour (450ms)
//   20    a sheen glides across; the face slides out under a blur (260ms)
//   50    the reward springs in from the side, -2% overshoot (310ms)
//   310   the amount rolls in digit by digit, like the budget card's figures
//         (helpers/digit_roll), and pulses on settle
//   650   a line along the bottom retracts over the hold
//   1400  the card flies off and the next one rises
//
// Skip and Stop change server state, so closing the deck after either reloads
// the dashboard (the same reload the row celebrations ended with), which is
// also where the streak +1 card those endpoints flag gets shown.
const KINDS = ["skip", "stop", "paid"]

export default class extends Controller {
  static targets = ["stage", "mascot", "segs", "reveal", "active", "sub", "card", "done", "tally", "haptic"]
  static values = { auto: Boolean, today: String, money: Object, subTemplate: String, paywallUrl: String }

  // Mirrors streak_up_controller: stamped when raised so a restored Turbo
  // snapshot does not raise the deck a second time.
  static RAISED_ATTRIBUTE = "data-due-deck-raised"
  static SHOWN_KEY = "floosyo:due-deck-shown"

  static SHOW_DELAY = 600
  // The row celebration holds 3200ms; a deck is gone through card after card.
  static HOLD = 750
  static SWIPE_X = 90
  static SWIPE_Y = 80

  static EASE_OUT = "cubic-bezier(0.22, 1, 0.36, 1)"
  static EASE_SPRING = "cubic-bezier(0.2, 1.55, 0.35, 1)"

  // Solid washes: a translucent one lets the card behind show through.
  static TINT = { skip: "#DDFBEC", stop: "#E2EEFE", paid: "#E5EAF3" }
  static SHEEN = { skip: "rgba(52, 211, 153, 0.22)", stop: "rgba(61, 138, 254, 0.18)", paid: "rgba(148, 163, 184, 0.25)" }
  static BAR = {
    skip: "linear-gradient(90deg, rgba(52, 211, 153, 0.45), rgba(52, 211, 153, 0.9))",
    stop: "linear-gradient(90deg, rgba(61, 138, 254, 0.35), rgba(61, 138, 254, 0.85))",
    paid: "linear-gradient(90deg, rgba(15, 23, 42, 0.25), rgba(15, 23, 42, 0.7))"
  }

  connect() {
    this._timers = []
    this._handled = new Map()
    this._requests = []
    this._serverChanged = false
    this._busy = false
    this._session = []

    this._resetForCache = () => this._reset()
    document.addEventListener("turbo:before-cache", this._resetForCache)

    prunePaid(this.todayValue)
    if (this.element.hasAttribute(this.constructor.RAISED_ATTRIBUTE)) return
    if (!this.autoValue || this._shownToday || this._pending().length === 0) return

    this._at(this.constructor.SHOW_DELAY, () => {
      if (!openOverlayScrim()) this._show()
    })
  }

  disconnect() {
    document.removeEventListener("turbo:before-cache", this._resetForCache)
    this._clearTimers()
    this._unlockScroll()
  }

  // ── opening ───────────────────────────────────────────────────

  open() {
    if (!this.element.hidden) return
    this._show()
  }

  _show() {
    const cards = this._pending()
    if (cards.length === 0) return

    this._session = cards
    this._markShown()
    this.element.setAttribute(this.constructor.RAISED_ATTRIBUTE, "1")
    this.element.hidden = false
    this._lockScroll()
    openChrome(this, this.element)
    // Same double-rAF re-assert as streak_up_controller#show: the budget
    // carousel shows the tab bar for its first slide during load.
    requestAnimationFrame(() => requestAnimationFrame(() => openChrome(this, this.element)))

    this._render()
    this._entrance()
  }

  _entrance() {
    if (this._reducedMotion) {
      this.mascotTarget.style.cssText = "opacity: 1; transform: none"
      this.revealTargets.forEach((el) => { el.style.opacity = "1" })
      return
    }

    this.stageTarget.animate(
      [{ transform: "scale(0.9)", opacity: 0 }, { transform: "scale(1)", opacity: 1 }],
      { duration: 550, delay: 120, easing: "cubic-bezier(0.2, 1.05, 0.3, 1)", fill: "both" }
    )
    this.mascotTarget.animate(
      [{ transform: "translateY(30px) scale(0.7)", opacity: 0 }, { transform: "translateY(0) scale(1)", opacity: 1 }],
      { duration: 620, delay: 420, easing: this.constructor.EASE_SPRING, fill: "both" }
    )
    this.revealTargets.forEach((el, i) => {
      el.animate(
        [{ opacity: 0, transform: "translateY(10px)" }, { opacity: 1, transform: "translateY(0)" }],
        { duration: 460, delay: 620 + i * 70, easing: this.constructor.EASE_OUT, fill: "both" }
      )
    })
  }

  // ── rendering ─────────────────────────────────────────────────

  _render() {
    const left = this._session.filter((card) => !this._handled.has(card.dataset.id))

    this.segsTarget.replaceChildren(...this._session.map((card) => {
      const seg = document.createElement("span")
      const kind = this._handled.get(card.dataset.id)
      seg.className = kind ? `due-deck-seg is-${kind}` : "due-deck-seg"
      return seg
    }))

    if (left.length === 0) {
      this._renderDone()
      return
    }

    this.activeTarget.hidden = false
    this.doneTarget.hidden = true

    const total = left.reduce((sum, card) => sum + Number(card.dataset.amount), 0)
    this.subTarget.innerHTML = this.subTemplateValue.replace("__AMOUNT__", moneyText(total, this.moneyValue))

    // One card at a time: the segments and the subtitle already say how many.
    const top = left[0]
    this.cardTargets.forEach((card) => {
      const arriving = card === top && card.hidden
      card.hidden = card !== top
      if (card !== top) return

      card.dataset.depth = 0
      card.style.transform = "translateY(0) scale(1)"
      if (arriving && !this._reducedMotion && this._session.length > left.length) this._arrive(card)
    })
  }

  // The next card fades up into the empty slot the last one flew out of.
  _arrive(card) {
    card.animate(
      [{ opacity: 0, transform: "scale(0.96)" }, { opacity: 1, transform: "scale(1)" }],
      { duration: 210, easing: this.constructor.EASE_OUT }
    )
  }

  _renderDone() {
    this.activeTarget.hidden = true
    this.doneTarget.hidden = false

    const counts = { skip: 0, stop: 0, paid: 0 }
    this._session.forEach((card) => { counts[this._handled.get(card.dataset.id)] += 1 })
    this.tallyTargets.forEach((tally) => {
      const count = counts[tally.dataset.kind]
      tally.hidden = count === 0
      tally.querySelector("b").textContent = count
    })

    if (this._reducedMotion) return
    this.doneTarget.animate(
      [{ opacity: 0, transform: "translateY(10px)" }, { opacity: 1, transform: "translateY(0)" }],
      { duration: 460, easing: this.constructor.EASE_OUT }
    )
  }

  // ── actions ───────────────────────────────────────────────────

  skip() { this._act("skip") }
  stop() { this._act("stop") }
  paid() { this._act("paid") }

  _act(kind) {
    const card = this._top()
    if (!card || this._busy) return

    this._busy = true
    this._fireHaptic()

    this._serverChanged = true
    const request = this._post(card.dataset[`${kind}Url`])
    this._requests.push(request)
    request.then((status) => this._settle(card, kind, status))
  }

  // Resolves to the response status, or 0 when the request never landed.
  _post(url) {
    const token = document.querySelector('meta[name="csrf-token"]')?.content
    return fetch(url, {
      method: "POST",
      credentials: "same-origin",
      headers: { "X-CSRF-Token": token, Accept: "application/json" }
    }).then((response) => response.status, () => 0)
  }

  // Celebrates only what the server accepted. A lapsed subscription (403)
  // goes to the paywall, like the old Skip/Stop buttons did. Any other refusal
  // leaves the card where it is; the reload on close shows the server's truth.
  _settle(card, kind, status) {
    if (this.element.hidden) return

    if (status === 403) {
      this._clearTimers()
      this.element.hidden = true
      this._unlockScroll()
      closeChromeThenNavigate(this, () => window.Turbo.visit(this.paywallUrlValue))
      return
    }

    if (status < 200 || status >= 300) {
      this._busy = false
      return
    }

    // Paid writes no row, so the phone still has to remember to hide it.
    if (kind === "paid") markPaid(card.dataset.id, this.todayValue)
    this._handled.set(card.dataset.id, kind)
    this._celebrate(card, kind)
  }

  // ── celebration (skip_celebration_controller, inside the card) ──

  _celebrate(card, kind) {
    const face = card.querySelector(".due-deck-face")
    const reward = card.querySelector(`.due-deck-reward[data-kind="${kind}"]`)
    const sheen = card.querySelector(".due-deck-sheen")
    const progress = card.querySelector(".due-deck-progress")
    const count = reward.querySelector("[data-roll]")
    const { x, y } = this._enterVector(kind)

    reward.hidden = false

    if (this._reducedMotion) {
      face.style.opacity = "0"
      card.style.backgroundColor = this.constructor.TINT[kind]
      this._at(this.constructor.HOLD + 200, () => this._fly(card, kind))
      return
    }

    reward.style.transform = `translate(${x}%, ${y}%)`
    sheen.style.background = `linear-gradient(90deg, transparent 0%, ${this.constructor.SHEEN[kind]} 45%, transparent 90%)`
    progress.style.background = this.constructor.BAR[kind]
    // Held back until it rolls, so the figure never shows before its digits drop.
    if (count) count.style.visibility = "hidden"

    const ease = this.constructor.EASE_OUT
    const sign = x === 0 ? 1 : Math.sign(x)

    this._at(10, () => card.animate(
      [{ backgroundColor: "#fff" }, { backgroundColor: this.constructor.TINT[kind] }],
      { duration: 450, easing: ease, fill: "forwards" }
    ))
    this._at(20, () => {
      sheen.animate(
        [
          { opacity: 0, transform: `translateX(${-sign * 40}%)` },
          { opacity: 1, offset: 0.3 },
          { opacity: 1, transform: `translateX(${sign * 40}%)`, offset: 0.75 },
          { opacity: 0, transform: `translateX(${sign * 60}%)` }
        ],
        { duration: 390, easing: ease, fill: "forwards" }
      )
      face.animate(
        [
          { transform: "translate(0, 0)", opacity: 1, filter: "blur(0px)" },
          { transform: `translate(${-x}%, ${-y}%)`, opacity: 0, filter: "blur(2.5px)" }
        ],
        { duration: 260, easing: "cubic-bezier(0.45, 0, 0.35, 1)", fill: "forwards" }
      )
    })
    this._at(50, () => reward.animate(
      [
        { transform: `translate(${x}%, ${y}%)` },
        { transform: `translate(${x * -0.02}%, ${y * -0.02}%)`, offset: 0.78 },
        { transform: "translate(0, 0)" }
      ],
      { duration: 310, easing: ease, fill: "forwards" }
    ))
    if (count) this._at(310, () => this._roll(count))
    this._at(650, () => progress.animate(
      [
        { opacity: 0, transform: "scaleX(1)" },
        { opacity: 0.9, transform: "scaleX(1)", offset: 0.05 },
        { opacity: 0.9, transform: "scaleX(0)" }
      ],
      { duration: this.constructor.HOLD, easing: "linear", fill: "forwards" }
    ))
    this._at(650 + this.constructor.HOLD, () => this._fly(card, kind))
  }

  // Skip and Paid arrive from the start edge, Stop from the end edge: the
  // same horizontal slide for all three. Percentages of the card's own box.
  _enterVector(kind) {
    const start = this._rtl ? 110 : -110
    return { x: kind === "stop" ? -start : start, y: 0 }
  }

  // Digits drop into place right-most first, the budget card's roll, then the
  // figure pulses once they have all settled.
  _roll(el) {
    el.style.visibility = ""
    rollDigits(el, true, { restore: true })

    const digits = el.querySelectorAll(".pace-roll-col").length
    this._at(Math.max(0, digits - 1) * ROLL_STAGGER_MS + 420, () => el.animate(
      [{ transform: "scale(1)" }, { transform: "scale(1.16)", offset: 0.5 }, { transform: "scale(1)" }],
      { duration: 220, easing: this.constructor.EASE_OUT }
    ))
  }

  // The card leaves toward its button, then the next one rises into place.
  _fly(card, kind) {
    const start = this._rtl ? 1 : -1
    const exits = {
      skip: `translateX(${start * 130}%) rotate(${start * 16}deg)`,
      stop: `translateX(${-start * 130}%) rotate(${-start * 16}deg)`,
      paid: "translateY(130%) scale(0.9)"
    }

    card.style.transition = "transform 0.225s cubic-bezier(0.5, 0, 0.75, 0), opacity 0.2s ease"
    card.style.transform = exits[kind]
    card.style.opacity = "0"

    this._at(190, () => {
      card.hidden = true
      this._busy = false
      this._render()
    })
  }


  // ── swipe ─────────────────────────────────────────────────────

  dragStart(event) {
    const card = event.currentTarget
    if (this._busy || card !== this._top()) return

    this._drag = { card, x: event.clientX, y: event.clientY, dx: 0, dy: 0 }
    card.classList.add("is-dragging")
    card.setPointerCapture?.(event.pointerId)
  }

  dragMove(event) {
    const drag = this._drag
    if (!drag) return

    const dx = event.clientX - drag.x
    const dy = Math.max(0, event.clientY - drag.y)
    drag.dx = dx
    drag.dy = dy

    const side = this._sideOf(dx)
    const reach = Math.min(1, Math.abs(dx) / 70)
    drag.card.style.transform = `translate(${dx}px, ${dy}px) rotate(${dx / 20}deg)`
    this._stamp(drag.card, "skip").style.opacity = side === "skip" ? reach : 0
    this._stamp(drag.card, "stop").style.opacity = side === "stop" ? reach : 0
    this._stamp(drag.card, "paid").style.opacity = Math.min(1, dy / 60)
  }

  dragEnd() {
    const drag = this._drag
    if (!drag) return
    this._drag = null

    drag.card.classList.remove("is-dragging")
    KINDS.forEach((kind) => { this._stamp(drag.card, kind).style.opacity = 0 })
    drag.card.style.transform = "translateY(0) scale(1)"

    // Whichever way the swipe mostly went decides it.
    const sideways = Math.abs(drag.dx) >= drag.dy
    if (sideways && Math.abs(drag.dx) > this.constructor.SWIPE_X) this._act(this._sideOf(drag.dx))
    else if (!sideways && drag.dy > this.constructor.SWIPE_Y) this._act("paid")
  }

  // Skip's tile sits on the start edge, Stop's on the end edge.
  _sideOf(dx) {
    if (dx === 0) return null
    const towardStart = this._rtl ? dx > 0 : dx < 0
    return towardStart ? "skip" : "stop"
  }

  _stamp(card, kind) {
    return card.querySelector(`.due-deck-stamp.is-${kind}`)
  }

  // ── closing ───────────────────────────────────────────────────

  // The X and the all-clear CTA. Announces like the other dashboard cards (the
  // rating ask waits on it, see review_prompt_controller#connect).
  close() {
    this._clearTimers()
    this._busy = false
    this.element.hidden = true
    this._unlockScroll()
    document.dispatchEvent(new CustomEvent("celebration:closed"))

    if (!this._serverChanged) {
      closeChrome(this)
      return
    }

    // Let every skip/stop land before the reload reads it back.
    Promise.allSettled(this._requests).then(() => {
      closeChromeThenNavigate(this, () => window.Turbo.visit(window.location.href, { action: "replace" }))
    })
  }

  // turbo:before-cache: hidden and unlocked, still marked raised.
  _reset() {
    this._clearTimers()
    this.element.hidden = true
    this._unlockScroll()
  }

  // ── helpers ───────────────────────────────────────────────────

  _pending() {
    const paid = paidIds(this.todayValue)
    return this.cardTargets.filter((card) => !this._handled.has(card.dataset.id) && !paid.has(card.dataset.id))
  }

  _top() {
    return this.cardTargets.find((card) => !card.hidden && card.dataset.depth === "0")
  }

  get _shownToday() {
    try {
      return localStorage.getItem(this.constructor.SHOWN_KEY) === this.todayValue
    } catch (_) {
      return false
    }
  }

  _markShown() {
    try {
      localStorage.setItem(this.constructor.SHOWN_KEY, this.todayValue)
    } catch (_) { }
  }

  get _rtl() {
    return document.documentElement.dir === "rtl"
  }

  get _reducedMotion() {
    return window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches
  }

  _fireHaptic() {
    if (!this.hasHapticTarget) return
    const haptic = this.application.getControllerForElementAndIdentifier(this.hapticTarget, "bridge--haptic")
    haptic?.tap()
  }

  _at(ms, fn) {
    this._timers.push(setTimeout(fn, ms))
  }

  _clearTimers() {
    this._timers.forEach((t) => clearTimeout(t))
    this._timers = []
  }

  _lockScroll() {
    this._prevOverflow = document.documentElement.style.overflow
    document.documentElement.style.overflow = "hidden"
  }

  // Only the lock this deck took is released (see review_prompt_controller).
  _unlockScroll() {
    if (this._prevOverflow === undefined) return

    document.documentElement.style.overflow = this._prevOverflow
    this._prevOverflow = undefined
  }
}
